/**
 * Possible double registrations within one camp.
 *
 * People who did not notice that their registration went through sign up again,
 * often with a second email address, so the email check cannot catch them. We
 * only flag them for the admins; the registrant is not told.
 */

export interface DuplicateCandidate {
  id: string
  status?: string | null
  createdAt?: string | null
  teilnehmer?: {
    id?: string
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    phone?: string | null
    birthday?: string | null
  } | null
}

export interface DuplicateHint {
  id: string
  email?: string | null
  status?: string | null
  createdAt?: string | null
  sameBirthday: boolean
  samePhone: boolean
}

export const normalizeName = (value?: string | null) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '')

const digitsOnly = (value?: string | null) => (value ?? '').replace(/\D/g, '')

export const birthdayKey = (value?: string | null) => {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
}

/**
 * Identity of a person across accounts: same first name, last name and birthday.
 * Empty when one of the three is missing, so incomplete records never match.
 */
export function personKey(
  person?: {
    firstName?: string | null
    lastName?: string | null
    birthday?: string | null
  } | null,
): string {
  const first = normalizeName(person?.firstName)
  const last = normalizeName(person?.lastName)
  const birthday = birthdayKey(person?.birthday)
  return first && last && birthday ? `${first}|${last}|${birthday}` : ''
}

/**
 * Maps a registration id to the other registrations of the same camp whose
 * participant has the same first and last name (ignoring case, accents and
 * punctuation). Registrations without a duplicate are not in the map.
 */
export function findDuplicateHints(registrations: DuplicateCandidate[]) {
  const byName = new Map<string, DuplicateCandidate[]>()

  for (const registration of registrations) {
    const first = normalizeName(registration.teilnehmer?.firstName)
    const last = normalizeName(registration.teilnehmer?.lastName)
    if (!first || !last) continue

    const key = `${first}|${last}`
    byName.set(key, [...(byName.get(key) ?? []), registration])
  }

  const hints = new Map<string, DuplicateHint[]>()

  for (const group of byName.values()) {
    if (group.length < 2) continue

    for (const registration of group) {
      const phone = digitsOnly(registration.teilnehmer?.phone)
      const birthday = birthdayKey(registration.teilnehmer?.birthday)

      hints.set(
        registration.id,
        group
          .filter((other) => other.id !== registration.id)
          .map((other) => ({
            id: other.id,
            email: other.teilnehmer?.email ?? null,
            status: other.status ?? null,
            createdAt: other.createdAt ?? null,
            sameBirthday: Boolean(birthday) && birthday === birthdayKey(other.teilnehmer?.birthday),
            samePhone: phone.length >= 6 && phone === digitsOnly(other.teilnehmer?.phone),
          })),
      )
    }
  }

  return hints
}

const STATUS_LABEL: Record<string, string> = {
  submitted: 'eingereicht',
  confirmed: 'bestätigt',
  waitlist: 'Warteliste',
  rejected: 'abgelehnt',
}

/** Tooltip text for the admin badge, e.g. "Gleicher Name wie a@b.at (eingereicht, 15.10. 00:22), gleiche Telefonnummer". */
export function describeDuplicateHints(hints: DuplicateHint[]): string {
  return hints
    .map((hint) => {
      const when = hint.createdAt
        ? new Date(hint.createdAt).toLocaleString('de-AT', {
            timeZone: 'Europe/Vienna',
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        : null
      const details = [(hint.status && STATUS_LABEL[hint.status]) || hint.status, when]
        .filter(Boolean)
        .join(', ')
      const same = [
        hint.sameBirthday && 'gleiches Geburtsdatum',
        hint.samePhone && 'gleiche Telefonnummer',
      ]
        .filter(Boolean)
        .join(', ')

      return [
        `Gleicher Name wie ${hint.email ?? 'andere Anmeldung'}${details ? ` (${details})` : ''}`,
        same,
      ]
        .filter(Boolean)
        .join(', ')
    })
    .join('\n')
}
